import * as RadixDialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import type { ReactNode } from 'react';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel?: string;
  // onConfirm may be sync or async. The dialog awaits its return so it can
  // (a) surface errors to the user instead of silently swallowing rejected
  // promises, and (b) keep the dialog open + show a busy state during a
  // long-running confirm.
  onConfirm: () => void | Promise<void>;
  destructive?: boolean;
  children?: ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  destructive = false,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (err) {
      // Keep the dialog open so the user sees something failed instead of
      // the previous fire-and-forget behavior where errors silently
      // disappeared and the dialog closed as if the action had succeeded.
      const msg = err instanceof Error ? err.message : String(err);
      console.error('ConfirmDialog action failed:', err);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <RadixDialog.Root
      open={open}
      onOpenChange={(next) => {
        // Block outside-click / Esc dismissal while the confirm action is
        // still running; otherwise the dialog can disappear mid-request and
        // any error would have nowhere to surface.
        if (busy && !next) return;
        if (!next) setError(null);
        onOpenChange(next);
      }}
    >
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <RadixDialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-surface border border-border rounded-lg shadow-popover p-6 w-96 z-50">
          <RadixDialog.Title className="text-lg font-semibold mb-2">{title}</RadixDialog.Title>
          <RadixDialog.Description className="text-text-muted mb-4">{description}</RadixDialog.Description>
          {error && (
            <div
              role="alert"
              className="mb-4 rounded border border-[color:var(--color-danger)] border-opacity-30 bg-danger-soft p-2 text-sm text-danger"
            >
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <RadixDialog.Close
              disabled={busy}
              className="px-3 py-1.5 border border-border rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelLabel}
            </RadixDialog.Close>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy}
              className={`px-3 py-1.5 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed ${destructive ? 'bg-danger' : 'bg-primary'}`}
            >
              {busy ? 'Working…' : confirmLabel}
            </button>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
